'use client';

import { useState } from 'react';
import { useBlogPosts } from '@/hooks/useBlog';
import Image from 'next/image';
import Link from 'next/link';
import { Calendar, ArrowRight, Newspaper } from 'lucide-react';
import { BlogCardSkeleton } from './components/BlogCardSkeleton';

export default function BlogListPageClient() {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useBlogPosts({
    page: 1,
    limit: 50,
    search: searchQuery,
    published: true,
  });

  const posts = data?.data || [];

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <main className="organic min-h-screen">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ padding: 'calc(64px + var(--space-8) * 1.6) clamp(20px, 4vw, 48px) var(--space-8)' }}
      >
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ right: -100, top: -120, width: 280, height: 280, background: 'var(--color-accent-2-200)', zIndex: 0 }}
        />
        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1, maxWidth: 640 }}>
          <span
            className="block uppercase mb-2"
            style={{ fontSize: 13, letterSpacing: '0.06em', fontWeight: 600, color: 'var(--color-accent-700)' }}
          >
            Sfaturi & inspirație
          </span>
          <h1 className="mb-3" style={{ fontSize: 'clamp(34px, 4.4vw, 54px)', lineHeight: 1.08, margin: '0 0 var(--space-3)' }}>
            Blog
          </h1>
          <p
            style={{
              fontSize: 16.5, lineHeight: 1.6,
              color: 'color-mix(in srgb, var(--color-text) 78%, transparent)',
              maxWidth: '52ch', margin: 0,
            }}
          >
            Ghiduri și povești despre flori, ocazii speciale și îngrijirea buchetelor.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto" style={{ padding: '0 clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)' }}>
        {/* Search */}
        <div
          className="flex items-center justify-between flex-wrap gap-3"
          style={{ marginBottom: 'var(--space-6)' }}
        >
          <input
            type="text"
            className="input"
            style={{ width: 'min(360px, 100%)' }}
            placeholder="Caută articole..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-6)' }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        ) : posts.length > 0 ? (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-6)' }}
          >
            {posts.map((post) => (
              <Link
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="card elev-sm"
                style={{ padding: 0, overflow: 'hidden' }}
              >
                <div className="relative" style={{ aspectRatio: '16 / 10' }}>
                  {post.coverImage ? (
                    <Image
                      src={post.coverImage}
                      alt={post.title}
                      fill
                      className="washed object-cover"
                      style={{ borderRadius: 0 }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full" style={{ background: 'var(--color-neutral-100)' }}>
                      <Newspaper className="h-16 w-16" style={{ color: 'var(--color-neutral-500)' }} />
                    </div>
                  )}
                  {post.category && (
                    <span
                      className="tag tag-accent absolute"
                      style={{ top: 12, right: 12 }}
                    >
                      {post.category}
                    </span>
                  )}
                </div>

                <div style={{ padding: 'var(--space-4)' }}>
                  <p className="card-title" style={{ margin: '0 0 var(--space-2)' }}>{post.title}</p>

                  {post.createdAt && (
                    <span
                      className="flex items-center gap-1.5"
                      style={{ fontSize: 13, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', marginBottom: 'var(--space-2)' }}
                    >
                      <Calendar className="h-3.5 w-3.5" strokeWidth={2.75} />
                      {formatDate(post.createdAt)}
                    </span>
                  )}

                  <p
                    className="line-clamp-3"
                    style={{ fontSize: 14, lineHeight: 1.5, color: 'color-mix(in srgb, var(--color-text) 75%, transparent)', margin: '0 0 var(--space-3)' }}
                  >
                    {post.excerpt}
                  </p>

                  <span
                    className="inline-flex items-center"
                    style={{ gap: 6, fontWeight: 600, fontSize: 14, color: 'var(--color-accent)' }}
                  >
                    Citește articolul
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.75} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center" style={{ padding: 'calc(var(--space-8) * 2) 0' }}>
            <h3 style={{ margin: '0 0 var(--space-2)' }}>Niciun articol găsit</h3>
            <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>
              {searchQuery ? 'Încearcă alți termeni de căutare.' : 'Revino curând pentru articole noi.'}
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
